import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { Assessment } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SectorCard, SectorRegimeSkeleton, fmtPct, fmtScore } from './shared';

const SectorRegimeSection = ({ loading, sectorRegime, sectorRegimeSummary, onSelectSector }) => {
  const { t } = useTranslation();

  if (loading && !sectorRegime) {
    return <SectorRegimeSkeleton />;
  }

  if (!sectorRegime || !Array.isArray(sectorRegime) || sectorRegime.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Assessment sx={{ fontSize: 18, color: '#64748b' }} />
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#1e293b' }}>
          {t('distribution.sectorRegime.title')}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
          — {t('distribution.sectorRegime.subtitle')}
        </Typography>
      </Box>

      {sectorRegimeSummary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(6,1fr)' }, gap: 1, mb: 1.25 }}>
          {[
            { label: t('distribution.sectorRegime.summaryRiskOn'), value: sectorRegimeSummary.risk_on_count ?? 0, color: '#10b981', sub: `${t('distribution.sectorRegime.aboveMa20')} ${fmtPct(sectorRegimeSummary.avg_pct_above_ma20, 0)}` },
            { label: t('distribution.sectorRegime.summaryWarning'), value: (sectorRegimeSummary.warning_count ?? 0) + (sectorRegimeSummary.profit_taking_count ?? 0), color: '#f59e0b', sub: t('distribution.sectorRegime.summaryWarningSub') },
            { label: t('distribution.sectorRegime.summaryDistribution'), value: (sectorRegimeSummary.distribution_count ?? 0) + (sectorRegimeSummary.strong_distribution_count ?? 0), color: '#f97316', sub: t('distribution.sectorRegime.summaryDistributionSub') },
            { label: t('distribution.sectorRegime.summaryRiskOff'), value: sectorRegimeSummary.risk_off_count ?? 0, color: '#ef4444', sub: t('distribution.sectorRegime.summaryRiskOffSub') },
            { label: t('distribution.sectorRegime.summaryRecovery'), value: sectorRegimeSummary.washout_recovery_count ?? 0, color: '#06b6d4', sub: t('distribution.sectorRegime.summaryRecoverySub') },
            { label: t('distribution.sectorRegime.summaryAvgScore'), value: fmtScore(sectorRegimeSummary.avg_sector_score), color: '#475569', sub: `${sectorRegimeSummary.total_sectors ?? 0} ${t('distribution.sectorRegime.members').toLowerCase()}` },
          ].map((item) => (
            <Box key={item.label} sx={{ borderRadius: 1.5, border: `1px solid ${alpha(item.color, 0.14)}`, bgcolor: '#fff', p: '10px 12px' }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.4 }}>{item.label}</Typography>
              <Typography sx={{ fontSize: '1.08rem', fontWeight: 900, color: item.color, fontFamily: 'monospace', lineHeight: 1 }}>{item.value}</Typography>
              <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', mt: 0.45 }}>{item.sub}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)', lg: 'repeat(5,1fr)' }, gap: 1 }}>
        {sectorRegime.map((sector) => (
          <SectorCard key={sector.sector} sector={sector} onClick={onSelectSector} />
        ))}
      </Box>
    </Box>
  );
};

export default SectorRegimeSection;